import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { DeleteOutline, Edit, AccountTree } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  hint: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  }
}));

const ChatFlows = () => {
  const classes = useStyles();
  const history = useHistory();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFlows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat-flows");
      setFlows(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const handleDelete = async id => {
    try {
      await api.delete(`/chat-flows/${id}`);
      toast.success(i18n.t("chatFlows.toasts.deleted"));
      loadFlows();
    } catch (err) {
      toastError(err);
    }
  };

  const patchFlow = async (flow, patch) => {
    const previous = flows;
    setFlows(list =>
      list.map(item => (item.id === flow.id ? { ...item, ...patch } : item))
    );
    try {
      await api.put(`/chat-flows/${flow.id}`, patch);
    } catch (err) {
      setFlows(previous);
      toastError(err);
    }
  };

  const toggleActive = flow => patchFlow(flow, { active: !flow.active });

  const toggleTriggers = flow =>
    patchFlow(flow, { triggersEnabled: flow.triggersEnabled === false });

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("chatFlows.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AccountTree />}
            onClick={() => history.push("/chat-flows/new")}
          >
            {i18n.t("chatFlows.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography className={classes.hint} variant="body2">
          {i18n.t("chatFlows.hint")}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("chatFlows.table.name")}</TableCell>
              <TableCell>{i18n.t("chatFlows.table.keyword")}</TableCell>
              <TableCell align="center">
                {i18n.t("chatFlows.table.triggers")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("chatFlows.table.active")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("chatFlows.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flows.map(flow => (
              <TableRow key={flow.id}>
                <TableCell>{flow.name}</TableCell>
                <TableCell>
                  {flow.triggersEnabled === false
                    ? i18n.t("chatFlows.table.manualOnly")
                    : (() => {
                        const t = String(flow.triggerType || "").toLowerCase();
                        if (t === "exact" || t === "keyword") {
                          const kws = Array.isArray(flow.triggerKeywords)
                            ? flow.triggerKeywords
                            : flow.keyword
                            ? [flow.keyword]
                            : [];
                          return `${t}: ${kws.join(", ") || "—"}`;
                        }
                        if ((flow.keyword || "").trim()) {
                          return `keyword: ${flow.keyword}`;
                        }
                        return "any";
                      })()}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={flow.triggersEnabled !== false}
                    onChange={() => toggleTriggers(flow)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={Boolean(flow.active)}
                    onChange={() => toggleActive(flow)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => history.push(`/chat-flows/${flow.id}`)}
                    title={i18n.t("chatFlows.buttons.openEditor")}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(flow.id)}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && flows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  {i18n.t("chatFlows.noFlows")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ChatFlows;
